import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Ticket, Search, ArrowUpDown, ArrowUp, ArrowDown, Download, RefreshCw } from 'lucide-react';
import axiosInstance from '../lib/axios';
import { getStatusColor, getPriorityColor, formatDateTimeBySetting } from '../lib/utils';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { generateTicketsListPDF } from '../utils/ticketsListPDFGenerator';

// Sub-component for Table Headers to reduce repetitive code
const SortableHeader = ({ field, label, currentSort, direction, onSort }) => (
  <th className="text-left p-4 font-semibold text-sm whitespace-nowrap">
    <button
      onClick={() => onSort(field)}
      className="flex items-center gap-1 hover:text-primary transition-colors font-bold"
    >
      {label}
      {currentSort === field ? (
        direction === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
      ) : (
        <ArrowUpDown className="w-4 h-4 opacity-30" />
      )}
    </button>
  </th>
);

const Tickets = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // --- Data State ---
  const [rawTickets, setRawTickets] = useState([]); // Data from API
  const [projects, setProjects] = useState([]);
  const [projectsMap, setProjectsMap] = useState({});
  const [floorsMap, setFloorsMap] = useState({});
  const [categories, setCategories] = useState([]);
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [loading, setLoading] = useState(true);
  const [isFetchingList, setIsFetchingList] = useState(false);

  // --- Filter State ---
  // Server-side filters (Triggers API call)
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  // Client-side filters (Instant filtering)
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // --- Sorting & Pagination ---
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Helper translations
  const translateStatus = (status) => t(`tickets.statuses.${status}`) || status;
  const translatePriority = (priority) => t(`tickets.priorities.${priority}`) || priority;

  // 1. Initial Load: Fetch Metadata (Projects, Floors, Settings)
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [projectsRes, categoriesRes, floorsRes, settingsRes] = await Promise.all([
          axiosInstance.get('/projects'),
          axiosInstance.get('/categories'),
          axiosInstance.get('/floors'),
          axiosInstance.get('/settings')
        ]);

        setProjects(projectsRes.data);
        setCategories(categoriesRes.data);
        setDateFormat(settingsRes.data.date_format || 'MM/DD/YYYY');

        // Maps for O(1) Lookup
        const pMap = {};
        projectsRes.data.forEach(p => { pMap[p.id] = p.name; });
        setProjectsMap(pMap);

        const fMap = {};
        floorsRes.data.forEach(f => { fMap[f.id] = f.name; });
        setFloorsMap(fMap);

      } catch (error) {
        toast.error(t('messages.error.fetchFailed'));
      }
    };
    fetchMetadata();
  }, []); // Run once on mount

  // 2. Fetch Tickets (Triggered only when Server Filters change)
  useEffect(() => {
    fetchTicketsFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, projectFilter, timeFilter, customDateFrom, customDateTo, location.pathname]);

  const fetchTicketsFromApi = async () => {
    setIsFetchingList(true);
    try {
      const params = new URLSearchParams();
      if (projectFilter !== 'all') params.append('project_id', projectFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      // Handle time filter
      if (timeFilter === 'custom' && customDateFrom && customDateTo) {
        params.append('date_from', customDateFrom);
        params.append('date_to', customDateTo);
      } else if (timeFilter !== 'all') {
        const now = new Date();
        let dateFrom;
        switch (timeFilter) {
          case 'today':
            dateFrom = now.toISOString().split('T')[0];
            params.append('date_from', dateFrom);
            params.append('date_to', dateFrom);
            break;
          case 'week':
            const lastWeek = new Date(now);
            lastWeek.setDate(now.getDate() - 7);
            dateFrom = lastWeek.toISOString().split('T')[0];
            params.append('date_from', dateFrom);
            break;
          case 'month':
            const lastMonth = new Date(now);
            lastMonth.setMonth(now.getMonth() - 1);
            dateFrom = lastMonth.toISOString().split('T')[0];
            params.append('date_from', dateFrom);
            break;
          default: break;
        }
      }

      const response = await axiosInstance.get(`/tickets?${params.toString()}`);
      setRawTickets(response.data); // Store raw data
    } catch (error) {
      console.error(error);
      toast.error(t('messages.error.fetchFailed'));
    } finally {
      setIsFetchingList(false);
      setLoading(false);
    }
  };

  // 3. Process Data (Filtering & Sorting on Client Side) - High Performance
  const processedTickets = useMemo(() => {
    // A. Enrich Data (Attach Names)
    let data = rawTickets.map(ticket => ({
      ...ticket,
      floor_name: floorsMap[ticket.floor_id] || '...',
      project_name: projectsMap[ticket.project_id] || '...'
    }));

    // B. Apply Client Filters
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      data = data.filter(t => 
        t.title?.toLowerCase().includes(lowerTerm) || 
        t.description?.toLowerCase().includes(lowerTerm) || 
        t.ticket_code?.toLowerCase().includes(lowerTerm)
      );
    }
    if (priorityFilter !== 'all') {
      data = data.filter(t => t.priority === priorityFilter);
    }
    if (categoryFilter !== 'all') {
      data = data.filter(t => t.category === categoryFilter);
    }

    // C. Apply Sorting
    data.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle strings case-insensitive
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      // Handle dates
      if (sortField === 'created_at') {
        aVal = new Date(a[sortField]);
        bVal = new Date(b[sortField]);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [rawTickets, floorsMap, projectsMap, searchTerm, priorityFilter, categoryFilter, sortField, sortDirection]);

  // 4. Pagination Logic
  const paginatedTickets = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return processedTickets.slice(start, start + itemsPerPage);
  }, [processedTickets, currentPage, itemsPerPage]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, priorityFilter, categoryFilter, statusFilter, projectFilter]);

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  const handleDownloadPDF = async () => {
    try {
      toast.loading(t('tickets.generatingPDF'));
      await generateTicketsListPDF(
        processedTickets, // Print filtered/sorted list
        t,
        dateFormat,
        formatDateTimeBySetting,
        translateStatus,
        translatePriority,
        getStatusColor,
        getPriorityColor,
        i18n.language,
        { searchTerm, projectFilter, statusFilter }
      );
      toast.dismiss();
      toast.success(t('tickets.pdfDownloaded'));
    } catch (error) {
      toast.dismiss();
      toast.error(t('tickets.pdfError'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">{t('tickets.title')}</h1>
          <div className="flex items-center gap-2 text-muted-foreground mt-2">
            <p>{t('tickets.allTickets')}</p>
            {isFetchingList && <RefreshCw className="w-3 h-3 animate-spin" />}
          </div>
        </div>
        <Button onClick={handleDownloadPDF} variant="outline" className="gap-2 w-full md:w-auto">
          <Download className="w-4 h-4" />
          {t('tickets.downloadPDF') || 'Download PDF'}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {/* Search - Filters instantly (Client side) */}
            <div className="relative col-span-2 md:col-span-3 lg:col-span-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('tickets.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-10 text-sm"
              />
            </div>
            
            {/* Project - Triggers API call */}
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="h-10 text-sm"><SelectValue placeholder={t('navigation.projects')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('tickets.filters.allProjects')}</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            
            {/* Category - Filters instantly */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-10 text-sm"><SelectValue placeholder={t('tickets.category')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('tickets.filters.allCategories')}</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            
            {/* Status - Triggers API call */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-10 text-sm"><SelectValue placeholder={t('tickets.status')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('tickets.filters.allStatuses')}</SelectItem>
                {['new', 'in_progress', 'fixed', 'rejected', 'closed', 'overdue'].map(s => (
                  <SelectItem key={s} value={s}>{translateStatus(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Priority - Filters instantly */}
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="h-10 text-sm"><SelectValue placeholder={t('tickets.priority')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('tickets.filters.allPriorities')}</SelectItem>
                {['urgent', 'high', 'medium', 'low'].map(p => (
                   <SelectItem key={p} value={p}>{translatePriority(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Time Filter - Triggers API call */}
            <Select value={timeFilter} onValueChange={setTimeFilter}>
              <SelectTrigger className="h-10 text-sm"><SelectValue placeholder={t('tickets.filters.allTime')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('tickets.filters.allTime')}</SelectItem>
                <SelectItem value="today">{t('tickets.filters.today')}</SelectItem>
                <SelectItem value="week">{t('tickets.filters.thisWeek')}</SelectItem>
                <SelectItem value="month">{t('tickets.filters.thisMonth')}</SelectItem>
                <SelectItem value="custom">{t('tickets.filters.customRange')}</SelectItem>
              </SelectContent>
            </Select>
            
            {timeFilter === 'custom' && (
              <>
                <Input type="date" value={customDateFrom} onChange={(e) => setCustomDateFrom(e.target.value)} className="h-10 text-sm" />
                <Input type="date" value={customDateTo} onChange={(e) => setCustomDateTo(e.target.value)} className="h-10 text-sm" />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      {processedTickets.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Ticket className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-bold mb-2">{t('tickets.noTicketsFound')}</h3>
            <p className="text-muted-foreground">
              {rawTickets.length > 0 ? t('messages.noResultsFilter') : t('tickets.noTicketsFound')}
            </p>
          </div>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
             {/* Pagination Summary */}
            <div className="text-sm text-muted-foreground">
              Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, processedTickets.length)}</span> of <span className="font-medium">{processedTickets.length}</span> results
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground hidden md:inline">{t('tickets.show')}</span>
              <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                <SelectTrigger className="h-8 w-[70px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/20">
                    <SortableHeader field="ticket_code" label={t('tickets.ticketCode')} currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                    <SortableHeader field="title" label={t('tickets.ticketTitle')} currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                    <SortableHeader field="project_name" label={t('navigation.projects')} currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                    <SortableHeader field="floor_name" label={t('floors.floorPlan')} currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                    <SortableHeader field="status" label={t('tickets.status')} currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                    <SortableHeader field="priority" label={t('tickets.priority')} currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                    <SortableHeader field="category" label={t('tickets.category')} currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                    <SortableHeader field="created_at" label={t('tickets.createdAt')} currentSort={sortField} direction={sortDirection} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {paginatedTickets.map((ticket) => (
                    <tr key={ticket.id} className="border-b hover:bg-muted/50 transition-colors">
                      <td className="p-4">
                        <Link to={`/tickets/${ticket.id}`} className="text-primary hover:underline font-mono text-sm font-bold">
                          {ticket.ticket_code || 'N/A'}
                        </Link>
                      </td>
                      <td className="p-4 max-w-[200px]">
                        <Link to={`/tickets/${ticket.id}`} className="font-medium hover:underline block truncate" title={ticket.title}>
                          {ticket.title}
                        </Link>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">{ticket.project_name}</td>
                      <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">{ticket.floor_name}</td>
                      <td className="p-4">
                        <Badge className={getStatusColor(ticket.status)}>{translateStatus(ticket.status)}</Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className={`capitalize ${getPriorityColor(ticket.priority)}`}>
                          {translatePriority(ticket.priority)}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm capitalize">{ticket.category}</td>
                      <td className="p-4 text-sm text-muted-foreground whitespace-nowrap">
                        {formatDateTimeBySetting(ticket.created_at, dateFormat)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Simplified Pagination Controls */}
            {processedTickets.length > itemsPerPage && (
              <div className="border-t p-4 flex items-center justify-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                  {t('common.previous')}
                </Button>
                <span className="text-sm font-medium px-2">
                  Page {currentPage} of {Math.ceil(processedTickets.length / itemsPerPage)}
                </span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(processedTickets.length / itemsPerPage)))} disabled={currentPage >= Math.ceil(processedTickets.length / itemsPerPage)}>
                  {t('common.next')}
                </Button>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default Tickets;